import { useAppDispatch, useAppSelector } from 'store/hooks';
import * as React from 'react';
import { FormikHelpers } from 'formik';
import { Box, Stepper, Step, StepButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { MuiModal } from 'ui/MuiModal';
import { Column, Row } from 'ui/Flex';
import { useSnackbar } from 'notistack';
import {
    MESSAGE_MARKETING_AUDIENCE_ADD_TRIGGERS_SUCCESS,
    MESSAGE_MARKETING_AUDIENCE_ADD_TRIGGERS_ERROR
} from 'config/messages';
import { AudienceCreateRequestData, createAudience } from 'store/audiences/audiencesSlice';
import { rulesApi } from 'components/rules/rulesApi';
import { getTimeUnitsToSeconds } from 'lib/timeHelpers';
import { AdjustmentScheme } from 'components/adjustment-schemes/adjustmentSchemeModel';
import { Perk } from 'components/loyalty/models/PerkModel';
import { Location } from 'components/location/models/LocationModel';
import { SubscriptionScheme } from 'components/subscription-schemes/subscriptionSchemeModel';
import { ESegmentMethodType, makeCreateRuleBody } from 'components/audience/models/addTriggersModel';
import { AudienceTriggersBaseForm, IAudienceAddTriggersFormData } from '../forms/AudienceTriggersBaseForm';
import { Audience, AudienceType, isAudience } from '../models/segmentModel';
import { AudienceCreateForm, AudienceCreateFormData } from '../forms/AudienceCreateForm';

const StyledColumn = styled(Column)(({ theme }) => ({
    gap: theme.spacing(2)
}));

const steps = [
    'Create Audience',
    'Create trigger to add customer to audience',
    'Create trigger to remove customer from audience'
];

type IProps = {
    onClose: () => void;
    locations: Array<Location>;
    audiences: Array<Audience>;
    perks: Array<Perk>;
    adjustmentSchemes: Array<AdjustmentScheme>;
    subscriptionSchemes: Array<SubscriptionScheme>;
    initialData?: Partial<AudienceCreateFormData>;
};

const AudienceCreationStepperContent: React.FC<IProps> = ({
    locations,
    audiences,
    perks,
    adjustmentSchemes,
    subscriptionSchemes,
    onClose
}: IProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useAppDispatch();
    const { isLoading } = useAppSelector(state => state.audiences);

    const [activeStep, setActiveStep] = React.useState(0);
    const [audienceData, setAudienceData] = React.useState<Partial<Audience> | null>(null);

    const handleCreateSave = React.useCallback(
        async ({ expires, measure, ...rest }: AudienceCreateFormData) => {
            const createBody: AudienceCreateRequestData = {
                ...rest
            };
            if (rest.type !== AudienceType.PERSISTENT) {
                createBody.expiryHorizon = getTimeUnitsToSeconds(Number(expires), measure);
            }
            const audience = await dispatch(createAudience(createBody));
            if (!!audience && isAudience(audience)) {
                setAudienceData(audience);
                setActiveStep(activeStep + 1);
            }
        },
        [dispatch, activeStep]
    );

    const handleAddStepSave = React.useCallback(
        async (
            id: string,
            values: IAudienceAddTriggersFormData,
            formikHelpers: FormikHelpers<IAudienceAddTriggersFormData>
        ) => {
            try {
                const response = await rulesApi.create({
                    body: makeCreateRuleBody(values, id, ESegmentMethodType.ADD_MEMBER)
                });

                if (response.ok) {
                    setActiveStep(activeStep + 1);
                    enqueueSnackbar(MESSAGE_MARKETING_AUDIENCE_ADD_TRIGGERS_SUCCESS, {
                        variant: 'success'
                    });
                } else {
                    enqueueSnackbar(MESSAGE_MARKETING_AUDIENCE_ADD_TRIGGERS_ERROR, { variant: 'error' });
                }
            } catch (e) {
                console.error(e);
                enqueueSnackbar(MESSAGE_MARKETING_AUDIENCE_ADD_TRIGGERS_ERROR, { variant: 'error' });
            } finally {
                formikHelpers.setSubmitting(false);
            }
        },
        [activeStep, enqueueSnackbar]
    );

    const handleRemoveStepSave = React.useCallback(
        async (
            id: string,
            values: IAudienceAddTriggersFormData,
            formikHelpers: FormikHelpers<IAudienceAddTriggersFormData>
        ) => {
            try {
                const response = await rulesApi.create({
                    body: makeCreateRuleBody(values, id, ESegmentMethodType.REMOVE_MEMBER)
                });

                if (response.ok) {
                    enqueueSnackbar(MESSAGE_MARKETING_AUDIENCE_ADD_TRIGGERS_SUCCESS, {
                        variant: 'success'
                    });
                    onClose();
                } else {
                    enqueueSnackbar(MESSAGE_MARKETING_AUDIENCE_ADD_TRIGGERS_ERROR, { variant: 'error' });
                }
            } catch (e) {
                console.error(e);
                enqueueSnackbar(MESSAGE_MARKETING_AUDIENCE_ADD_TRIGGERS_ERROR, { variant: 'error' });
            } finally {
                formikHelpers.setSubmitting(false);
            }
        },
        [onClose, enqueueSnackbar]
    );

    const handleSkip = React.useCallback(() => {
        setActiveStep(prev => {
            if (prev === 2) {
                onClose();
                return prev;
            }
            return prev + 1;
        });
    }, [onClose]);

    return (
        <Box width="100%" maxWidth={1600}>
            <StyledColumn>
                <Row valign="flex-start" align="center">
                    <Box flex={1} maxWidth={500}>
                        <Stepper nonLinear alternativeLabel activeStep={activeStep}>
                            {steps.map((item, index) => (
                                <Step key={`step-button-${item}`} completed={activeStep > index}>
                                    <StepButton disableRipple disabled color="inherit" />
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                </Row>
                {activeStep === 0 && (
                    <AudienceCreateForm onSubmit={handleCreateSave} isLoading={isLoading} onClose={onClose} />
                )}
                {activeStep === 1 && (
                    <AudienceTriggersBaseForm
                        formTitle={
                            <Typography color="textSecondary" variant="h6">
                                Create trigger to <b>add</b> customer to audience
                            </Typography>
                        }
                        locations={locations}
                        perks={perks}
                        adjustmentSchemes={adjustmentSchemes}
                        subscriptionSchemes={subscriptionSchemes}
                        audiences={audiences}
                        audienceData={audienceData}
                        onSubmit={handleAddStepSave}
                        onSkip={handleSkip}
                    />
                )}
                {activeStep === 2 && (
                    <AudienceTriggersBaseForm
                        formTitle={
                            <Typography color="textSecondary" variant="h6">
                                Create trigger to <b>remove</b> customer from audience
                            </Typography>
                        }
                        locations={locations}
                        perks={perks}
                        adjustmentSchemes={adjustmentSchemes}
                        subscriptionSchemes={subscriptionSchemes}
                        audiences={audiences}
                        audienceData={audienceData}
                        onSubmit={handleRemoveStepSave}
                        onSkip={handleSkip}
                    />
                )}
            </StyledColumn>
        </Box>
    );
};

export function AudienceCreationStepper({ open, ...props }: IProps & { open: boolean }) {
    return (
        <MuiModal open={open}>
            <AudienceCreationStepperContent {...props} />
        </MuiModal>
    );
}
